import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import type { ReactNode } from "react";
import { EASE_OUT } from "../theme";

interface DisclosureProps {
  /** Header text, e.g. "Advanced". */
  label: string;
  /** Current value/summary shown in the header, so a folded section still
   * reads ("Advanced · 3 overridden"). */
  value?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode: supply both to own the open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/** Collapsible "Advanced" drawer — the hairline header + `+`/`–` affordance. */
export function Disclosure({ label, value, children, defaultOpen = false, open, onOpenChange, className = "" }: DisclosureProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const isOpen = open ?? uncontrolled;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => { setUncontrolled(!isOpen); onOpenChange?.(!isOpen); }}
        aria-expanded={isOpen}
        className="flex items-center justify-between font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant hover:text-primary"
      >
        <span>{label}</span>
        <span className="flex items-baseline gap-2">
          {value !== undefined && value !== null ? (
            <span className="font-body text-[10px] normal-case tracking-normal text-outline">{value}</span>
          ) : null}
          <span>{isOpen ? "–" : "+"}</span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2">{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
